import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  cancelDeliveryPass,
  cancelTaxiPass,
  reinstateRecurringPass,
  revokeDomesticWorker,
  revokeRecurringPass,
  suspendDomesticWorker,
  suspendRecurringPass,
} from '@/services/api.client';
import type { DeliveryPass, RecurringPass, TaxiPass } from '@/types';
import { formatDate, formatDateTime, statusBadgeClass } from '@/lib/utils';

// Discriminated selection — the page that opens the sheet knows what it's
// showing, so we don't sniff at runtime. Keeping shapes explicit also keeps
// lifecycle action wiring type-safe per variant.
export type PassDetailSelection =
  | { kind: 'recurring'; pass: RecurringPass }
  | { kind: 'domestic_worker'; pass: RecurringPass }
  | { kind: 'delivery'; pass: DeliveryPass }
  | { kind: 'taxi'; pass: TaxiPass };

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  selection: PassDetailSelection | null;
}

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function TITLE_FOR(kind: PassDetailSelection['kind']): string {
  switch (kind) {
    case 'recurring':
      return 'Recurring Pass';
    case 'domestic_worker':
      return 'Domestic Worker Pass';
    case 'delivery':
      return 'Delivery Pass';
    case 'taxi':
      return 'Taxi Pass';
  }
}

// Small label/value row used across all variants — keeps the sheet legible
// without introducing a design-system-wide DefinitionList component for a
// single consumer.
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-[var(--border-subtle)] last:border-0">
      <span className="text-xs text-[var(--text-tertiary)] uppercase tracking-wide">{label}</span>
      <span className="text-sm text-[var(--text-primary)] text-right">{value}</span>
    </div>
  );
}

// ── Per-kind body + actions ─────────────────────────────────────────────────

function RecurringBody({
  pass,
  propertyId,
  isDomestic,
  onClose,
}: {
  pass: RecurringPass;
  propertyId: string;
  isDomestic: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const queryKey = isDomestic ? ['domestic-workers', propertyId] : ['recurring-passes', propertyId];
  const invalidate = () => void qc.invalidateQueries({ queryKey });

  const suspend = useMutation({
    mutationFn: () =>
      isDomestic
        ? suspendDomesticWorker(propertyId, pass.id)
        : suspendRecurringPass(propertyId, pass.id),
    onSuccess: () => {
      toast.success('Pass suspended');
      invalidate();
      onClose();
    },
    onError: () => toast.error('Failed to suspend pass'),
  });

  const reinstate = useMutation({
    mutationFn: () => reinstateRecurringPass(propertyId, pass.id),
    onSuccess: () => {
      toast.success('Pass reinstated');
      invalidate();
      onClose();
    },
    onError: () => toast.error('Failed to reinstate pass'),
  });

  const revoke = useMutation({
    mutationFn: () =>
      isDomestic
        ? revokeDomesticWorker(propertyId, pass.id)
        : revokeRecurringPass(propertyId, pass.id),
    onSuccess: (result) => {
      // revokeDomesticWorker returns void; revokeRecurringPass returns { id, status }.
      // Cast to the richer type so TypeScript doesn't resolve TData as never.
      const r = result as { status?: string } | undefined;
      if (!isDomestic && r?.status) {
        toast.success(`Pass ${r.status}`);
      } else {
        toast.success('Pass revoked');
      }
      invalidate();
      onClose();
    },
    onError: () => toast.error('Failed to revoke pass'),
  });

  const pending = suspend.isPending || reinstate.isPending || revoke.isPending;
  const terminal = pass.status === 'revoked' || pass.status === 'expired';
  const days = pass.scheduleDays?.map((d) => DAY_ABBR[d]).join(', ') ?? 'Daily';

  return (
    <>
      <div className="flex items-center gap-2">
        <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
        <span className="text-xs text-[var(--text-tertiary)]">
          Created {formatDate(pass.createdAt)}
        </span>
      </div>

      <div className="mt-4">
        <Row label="Visitor" value={pass.visitorName} />
        <Row label="Phone" value={pass.visitorPhone} />
        <Row label="Schedule" value={`${days} · ${pass.entryTimeStart}–${pass.entryTimeEnd}`} />
        <Row label="Gate" value={pass.gateName} />
        <Row label="Valid from" value={formatDate(pass.validFrom)} />
        <Row label="Valid until" value={pass.validUntil ? formatDate(pass.validUntil) : 'No end'} />
        {pass.notes && <Row label="Notes" value={pass.notes} />}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {pass.status === 'active' && (
          <Button variant="outline" onClick={() => suspend.mutate()} disabled={pending}>
            Suspend
          </Button>
        )}
        {pass.status === 'suspended' && (
          <Button onClick={() => reinstate.mutate()} disabled={pending}>
            Reinstate
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => revoke.mutate()}
          disabled={pending || terminal}
          className="text-[var(--status-red-text)] border-[var(--border-error)] hover:bg-[var(--status-red-tint)]"
        >
          Revoke
        </Button>
      </div>
    </>
  );
}

function DeliveryBody({
  pass,
  propertyId,
  onClose,
}: {
  pass: DeliveryPass;
  propertyId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => cancelDeliveryPass(propertyId, pass.id),
    onSuccess: () => {
      toast.success('Delivery pass cancelled');
      void qc.invalidateQueries({ queryKey: ['delivery-passes', propertyId] });
      onClose();
    },
    onError: () => toast.error('Failed to cancel delivery pass'),
  });

  const terminal =
    pass.status === 'used' || pass.status === 'expired' || pass.status === 'cancelled';

  return (
    <>
      <div className="flex items-center gap-2">
        <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
        <span className="text-xs text-[var(--text-tertiary)]">
          Created {formatDate(pass.createdAt)}
        </span>
      </div>

      <div className="mt-4">
        {pass.courierCompany && <Row label="Company" value={pass.courierCompany} />}
        {pass.courierName && <Row label="Courier" value={pass.courierName} />}
        <Row label="Valid from" value={pass.validFrom ? formatDateTime(pass.validFrom) : '—'} />
        <Row label="Valid until" value={formatDateTime(pass.validUntil)} />
        {pass.usedAt && <Row label="Used at" value={formatDateTime(pass.usedAt)} />}
      </div>

      <div className="mt-6">
        <Button
          variant="outline"
          onClick={() => cancel.mutate()}
          disabled={terminal || cancel.isPending}
        >
          {cancel.isPending ? 'Cancelling…' : 'Cancel pass'}
        </Button>
      </div>
    </>
  );
}

function TaxiBody({
  pass,
  propertyId,
  onClose,
}: {
  pass: TaxiPass;
  propertyId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const cancel = useMutation({
    mutationFn: () => cancelTaxiPass(propertyId, pass.id),
    onSuccess: () => {
      toast.success('Taxi pass cancelled');
      void qc.invalidateQueries({ queryKey: ['taxi-passes', propertyId] });
      onClose();
    },
    onError: () => toast.error('Failed to cancel taxi pass'),
  });

  const terminal =
    pass.status === 'used' || pass.status === 'expired' || pass.status === 'cancelled';

  return (
    <>
      <div className="flex items-center gap-2">
        <Badge className={statusBadgeClass(pass.status)}>{pass.status.toUpperCase()}</Badge>
        <span className="text-xs text-[var(--text-tertiary)]">
          Created {formatDateTime(pass.createdAt)}
        </span>
      </div>

      <div className="mt-4">
        {pass.vehiclePlate && <Row label="Plate" value={pass.vehiclePlate} />}
        {pass.vehicleMake && <Row label="Make" value={pass.vehicleMake} />}
        {pass.driverName && <Row label="Driver" value={pass.driverName} />}
        {pass.ridePlatform && <Row label="Platform" value={pass.ridePlatform} />}
        {pass.rideReference && <Row label="Reference" value={pass.rideReference} />}
        <Row label="Valid until" value={formatDateTime(pass.validUntil)} />
        {pass.usedAt && <Row label="Used at" value={formatDateTime(pass.usedAt)} />}
      </div>

      <div className="mt-6">
        <Button
          variant="outline"
          onClick={() => cancel.mutate()}
          disabled={terminal || cancel.isPending}
        >
          {cancel.isPending ? 'Cancelling…' : 'Cancel pass'}
        </Button>
      </div>
    </>
  );
}

// ── Exported sheet ──────────────────────────────────────────────────────────

export function PassDetailSheet({ open, onOpenChange, propertyId, selection }: Props) {
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{selection ? TITLE_FOR(selection.kind) : 'Pass'}</SheetTitle>
        </SheetHeader>
        <div className="px-4">
          {selection?.kind === 'recurring' && (
            <RecurringBody
              pass={selection.pass}
              propertyId={propertyId}
              isDomestic={false}
              onClose={close}
            />
          )}
          {selection?.kind === 'domestic_worker' && (
            <RecurringBody
              pass={selection.pass}
              propertyId={propertyId}
              isDomestic={true}
              onClose={close}
            />
          )}
          {selection?.kind === 'delivery' && (
            <DeliveryBody pass={selection.pass} propertyId={propertyId} onClose={close} />
          )}
          {selection?.kind === 'taxi' && (
            <TaxiBody pass={selection.pass} propertyId={propertyId} onClose={close} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
